import styled, { type CSSObject } from '@emotion/styled';
import { fetchUserAttributes, getCurrentUser } from 'aws-amplify/auth';
import { forwardRef, useContext, useEffect, useState } from 'react';
import { AuthContext, SidebarContext } from '../../../contexts';
import { getUserInfoBySub } from '../../../models';
import { Scrollbar } from '../../LayoutComponents';
import { SidebarMenu } from './SidebarMenu';

const sidebarClasses = {
  root: 'av-sidebar-root',
  container: 'av-sidebar-container',
  collapsed: 'av-collapsed'
};

export interface SidebarProps extends React.HTMLAttributes<HTMLHtmlElement> {
  /**
   * width of the sidebar
   * @default ```250px```
   */
  width?: string;

  /**
   * width of the sidebar when collapsed
   * @default ```80px```
   */
  collapsedWidth?: string;

  /**
   * sidebar background color
   */
  backgroundColor?: string;

  /**
   * duration for the transition in milliseconds to be used in collapse and toggle behavior
   * @default ```300```
   */
  transitionDuration?: number;

  /**
   * sidebar styles to be applied from the root element
   */
  rootStyles?: CSSObject;

  children?: React.ReactNode;
}

interface StyledSidebarProps extends Omit<SidebarProps, 'backgroundColor'> {
  collapsed?: boolean;
  collapsedWidth?: string;
  toggled?: boolean;
  broken?: boolean;
  rtl?: boolean;
}

type StyledSidebarContainerProps = Pick<SidebarProps, 'backgroundColor'>;

const StyledSidebar = styled.aside<StyledSidebarProps>`
  position: relative;
  z-index: 6;
  top: 0;
  left: 0;
  height: 100vh;

  transition: ${({ transitionDuration }) => `width, left, right, ${transitionDuration}ms`};

  width: ${({ width }) => width};
  min-width: ${({ width }) => width};

  &.${sidebarClasses.collapsed} {
    width: ${({ collapsedWidth }) => collapsedWidth};
    min-width: ${({ collapsedWidth }) => collapsedWidth};
  }

  ${({ rootStyles }) => rootStyles}
`;

const StyledSidebarContainer = styled.div<StyledSidebarContainerProps>`
  position: relative;
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 3;

  ${({ backgroundColor }) => (backgroundColor ? `background-color:${backgroundColor};` : '')}
`;

export const Sidebar = forwardRef<HTMLHtmlElement, SidebarProps>(
  (
    {
      width = '250px',
      collapsedWidth = '80px',
      className,
      children,
      backgroundColor = '#005665',
      transitionDuration = 300,
      rootStyles,
      ...rest
    },
    ref
  ) => {
    const [mounted] = useState(false);
    const { setLoading, setAwsAuth, setAuth, auth } = useContext(AuthContext);
    const { collapsed } = useContext(SidebarContext);

    useEffect(() => {
      (async () => {
        setLoading(true);
        try {
          const { username } = await getCurrentUser();
          const attributes = await fetchUserAttributes();
          setAwsAuth(attributes);
          if (username && auth === undefined) {
            const userInfo = await getUserInfoBySub(username);
            setAuth(userInfo);
          }
        } catch (e) {
          console.error(e);
        } finally {
          setLoading(false);
        }
      })();
      //todo:v2-changes
    }, []);

    const collapsedValue = collapsed ?? !mounted;

    return (
      <StyledSidebar
        ref={ref}
        rootStyles={rootStyles}
        width={width}
        collapsedWidth={collapsedWidth}
        transitionDuration={transitionDuration}
        /* className={drawerClasses(
          sidebarClasses.root,
          {
            [sidebarClasses.collapsed]: collapsedValue
          },
          className
        )} */
        className={collapsedValue ? sidebarClasses.collapsed : ''}
        {...rest}
      >
        <StyledSidebarContainer className={sidebarClasses.container} backgroundColor={backgroundColor}>
          <Scrollbar>
            {children}
            <SidebarMenu wide={!collapsedValue} />
          </Scrollbar>
        </StyledSidebarContainer>
      </StyledSidebar>
    );
  }
);
